import { Bell, Clock, Megaphone, MessageSquare } from 'lucide-react';
import { Fragment } from 'react';
import { z } from 'zod';

import { defineStandalone } from '@apps/web/features/phoebe_chat_v3/registry/types';
import { EventLine } from '@apps/web/features/phoebe_chat_v3/ui/primitives/event_line';
import { OutreachStartedActionLine } from '@apps/web/features/phoebe_chat_v3/ui/primitives/outreach_start_line';
import { ResolvedReferenceText } from '@apps/web/features/phoebe_chat_v3/ui/primitives/reference_badge';
import { formatTime } from '@apps/web/lib/date_utils';

const PAST_COPY = 'Checked back on the request';

// An outreach-started poke is a narration of something that happened in
// the app (the Start Outreach button, auto-start), not a scheduled
// check-in, so it draws its own lines instead of the check-in copy.
const OUTREACH_STARTED_REASON = 'outreach_started';

// Producers also reuse the reason for skipped auto-starts, so the
// narration keys off the structured facts, not the reason alone. Events
// committed before the facts existed fall back to "started".
const outreachStartedFactsSchema = z
    .object({
        outcome: z.enum(['started', 'skipped']),
        // Auto-start enqueues as the system user, which still hydrates a
        // sent_by name — this flag keeps automated starts unattributed.
        automatic: z.boolean().optional(),
        contacted_caregiver_count: z.number().int().nonnegative(),
    })
    .strip();

const pokePayloadSchema = z
    .object({
        type: z.literal('poke').optional(),
        reason: z.string(),
        instructions: z.string(),
        fire_index: z.number().int().nonnegative().optional(),
        total_fires: z.number().int().positive().optional(),
        outreach_started: outreachStartedFactsSchema.nullish(),
    })
    .strip();

type OutreachStartedFacts = z.infer<typeof outreachStartedFactsSchema>;
type OutreachStartedOutcome = OutreachStartedFacts['outcome'];

type OutreachStartNarration = Readonly<{
    readonly outcome: OutreachStartedOutcome;
    readonly automatic: boolean;
    readonly contactedCaregiverCount: number | null;
}>;

// The single discriminator for outreach-start pokes: every lifecycle
// callback below keys off this, so the reason string is consulted in
// exactly one place. Facts on a non-outreach reason are ignored; an
// outreach reason without facts is a pre-facts committed event and
// reads as a plain start with no count.
function outreachStartNarration(
    payload: z.infer<typeof pokePayloadSchema>,
): OutreachStartNarration | null {
    if (payload.reason !== OUTREACH_STARTED_REASON) {
        return null;
    }
    return {
        outcome: payload.outreach_started?.outcome ?? 'started',
        automatic: payload.outreach_started?.automatic ?? false,
        contactedCaregiverCount:
            payload.outreach_started?.contacted_caregiver_count ?? null,
    };
}

function outreachStartedSummary(outcome: OutreachStartedOutcome): string {
    return outcome === 'skipped' ? 'Auto-start skipped' : 'Outreach started';
}

type OutreachStartedLinesProps = Readonly<{
    readonly outcome: OutreachStartedOutcome;
    readonly automatic: boolean;
    readonly userId: string | null;
    readonly displayName: string | null;
    readonly contactedCaregiverCount: number | null;
}>;

function OutreachStartedLines({
    outcome,
    automatic,
    userId,
    displayName,
    contactedCaregiverCount,
}: OutreachStartedLinesProps) {
    if (outcome === 'skipped') {
        return (
            <EventLine glyph={<Megaphone />}>
                Auto-start didn’t start this outreach
            </EventLine>
        );
    }
    return (
        <Fragment>
            <OutreachStartedActionLine
                automatic={automatic}
                userId={userId}
                displayName={displayName}
            />
            {contactedCaregiverCount !== null &&
                contactedCaregiverCount > 0 && (
                    <EventLine glyph={<MessageSquare />}>
                        Phoebe texted {contactedCaregiverCount}{' '}
                        {contactedCaregiverCount === 1
                            ? 'caregiver'
                            : 'caregivers'}
                    </EventLine>
                )}
        </Fragment>
    );
}

export const pokeDefinitions = {
    poke: defineStandalone('poke', {
        disposition: 'render',
        schema: pokePayloadSchema,
        malformed: {
            kind: 'placeholder',
            customerCopy: PAST_COPY,
        },
        evidence: null,
        group: {
            kind: 'activity',
            boundary: 'consecutive_same_kind',
            receipt: true,
            // A check-in stays pending until the server drains it, even
            // past its scheduled time: only the committed event proves it
            // fired, so the stack keeps its row (reading "now") until then.
            // An outreach start already happened, so it is never pending
            // work for the "What Phoebe is working on" stack.
            phase(payload, _compoundState, occurrence) {
                if (outreachStartNarration(payload) !== null) {
                    return 'completed';
                }
                return occurrence.phase === 'pending' &&
                    occurrence.pending?.status === 'pending'
                    ? 'active'
                    : 'completed';
            },
            presentSummary(payload) {
                const narration = outreachStartNarration(payload);
                if (narration !== null) {
                    return outreachStartedSummary(narration.outcome);
                }
                return `Checking back · ${payload.reason}`;
            },
            pastSummary(payload) {
                const narration = outreachStartNarration(payload);
                if (narration !== null) {
                    return outreachStartedSummary(narration.outcome);
                }
                return `Checked back · ${payload.reason}`;
            },
            activeWork(_payload, _compoundState, occurrence) {
                const scheduledAtMs = Date.parse(
                    occurrence.pending?.created_at ?? '',
                );
                const fireAtMs = Date.parse(
                    occurrence.pending?.visible_at ?? '',
                );
                return {
                    kind: 'check_in',
                    scheduledAtMs: Number.isFinite(scheduledAtMs)
                        ? scheduledAtMs
                        : fireAtMs,
                    fireAtMs,
                };
            },
        },
        render(payload, occurrence, context) {
            // The agent cancelled the check-in before it fired. A receipt
            // for it would claim work that never happened, so it draws
            // nothing and leaves the transcript.
            if (occurrence.phase === 'rejected') {
                return null;
            }
            const narration = outreachStartNarration(payload);
            if (narration !== null) {
                const sentBy = occurrence.committed?.sent_by ?? null;
                return (
                    <OutreachStartedLines
                        outcome={narration.outcome}
                        automatic={narration.automatic}
                        userId={
                            sentBy?.user_id ??
                            occurrence.pending?.enqueued_by_user_id ??
                            null
                        }
                        displayName={sentBy?.display_name ?? null}
                        contactedCaregiverCount={
                            narration.contactedCaregiverCount
                        }
                    />
                );
            }
            // "Fired" is the server's word, not the clock's: a pending
            // check-in past its time still reads as scheduled.
            const fired = occurrence.phase === 'committed';
            const occurredAt =
                occurrence.committed?.created_at ??
                occurrence.pending?.visible_at ??
                null;
            const time =
                occurredAt === null
                    ? null
                    : formatTime(occurredAt, context.organizationTimeZone);
            return (
                <EventLine glyph={fired ? <Bell /> : <Clock />}>
                    {fired ? 'Checked back' : 'Checking back'}
                    {time === null ? '' : ` at ${time}`} ·{' '}
                    <ResolvedReferenceText text={payload.reason} />
                </EventLine>
            );
        },
    }),
} as const;

export const pokeContractFixtures = {
    valid: {
        type: 'poke',
        reason: 'Internal retry reason',
        instructions: 'Internal follow-up instructions',
        fire_index: 0,
        total_fires: 2,
    },
    malformed: {
        type: 'poke',
        reason: 7,
        instructions: 'Internal follow-up instructions',
    },
} as const;
